import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Building2 } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useNotificationCenter } from '../../contexts/NotificationCenterContext';

const getPageTitle = (pathname = '') => {
  if (pathname.startsWith('/contracheques/')) return 'Contracheque';
  if (pathname.startsWith('/requerimentos')) return 'Solicitações';
  if (pathname.startsWith('/recadastramento')) return 'Recadastramento';
  if (pathname.startsWith('/ficha-funcional')) return 'Ficha Funcional';
  if (pathname.startsWith('/notificacoes')) return 'Central de Notificações';
  if (pathname.startsWith('/avisos')) return 'Avisos';
  if (pathname.startsWith('/ajuda')) return 'Atendimento ao DP';
  if (pathname.startsWith('/change-password')) return 'Alterar senha';
  return 'Início';
};

const getInitials = (value = '') => String(value || 'Servidor')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || 'S';

const Header = () => {
  const { user, vinculoSelecionado } = useAuth();
  const { unreadCount, hasUnread } = useNotificationCenter();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileLogoError, setMobileLogoError] = useState(false);

  const title = getPageTitle(location.pathname);
  const isDashboard = location.pathname === '/dashboard';
  const canGoBack = !isDashboard;
  const mobileLogoUrl = user?.logoUrl || '';
  const userName = user?.name || user?.nome || 'Servidor';
  const userInitials = getInitials(userName);
  const profilePhotoUrl = user?.photoURL || user?.fotoUrl || user?.avatarUrl || '';
  const municipioId = vinculoSelecionado?.municipioId || user?.municipioId || '';
  let cachedMunicipioName = '';
  try {
    cachedMunicipioName = municipioId ? window.localStorage.getItem(`sisdp_server_municipio_name:${municipioId}`) || '' : '';
  } catch (storageError) {
    cachedMunicipioName = '';
  }
  const municipioLabel = user?.municipioNome
    || user?.municipio
    || vinculoSelecionado?.municipioNome
    || vinculoSelecionado?.municipio
    || cachedMunicipioName
    || '';

  return (
    <header className="z-[60] flex h-[var(--server-header-height)] shrink-0 items-center justify-between border-b border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface)] px-3 sm:px-5 lg:px-6">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        {canGoBack ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="server-touch-target inline-flex shrink-0 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
            aria-label="Voltar"
          >
            <ArrowLeft size={21} aria-hidden="true" />
          </button>
        ) : (
          <Link
            to="/dashboard"
            className="server-reference-header-brand-logo"
            aria-label="Portal do Servidor — Início"
          >
            {mobileLogoUrl && !mobileLogoError ? (
              <img
                src={mobileLogoUrl}
                alt=""
                className="h-8 w-8 max-h-8 max-w-8 object-contain"
                onError={() => setMobileLogoError(true)}
                aria-hidden="true"
              />
            ) : 'PS'}
          </Link>
        )}

        <div className="min-w-0">
          <p className="truncate text-base font-extrabold text-[var(--sisdp-color-text)] sm:text-lg">
            {isDashboard ? 'Portal do Servidor' : title}
          </p>
          {isDashboard && municipioLabel ? (
            <p className="max-w-[12rem] truncate text-[11px] font-semibold text-[var(--sisdp-color-text-muted)] md:hidden">{municipioLabel}</p>
          ) : !isDashboard ? (
            <p className="hidden text-xs font-bold uppercase tracking-[0.12em] text-blue-600 dark:text-blue-300 sm:block">Portal do Servidor</p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        {isDashboard && municipioLabel && (
          <div className="server-reference-municipality-pill hidden md:inline-flex">
            <Building2 size={17} aria-hidden="true" />
            <span className="max-w-56 truncate">{municipioLabel}</span>
          </div>
        )}

        {!isDashboard && (
          <Link
            to="/notificacoes"
            className="server-touch-target relative inline-flex items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:text-gray-300 dark:hover:bg-blue-950/40 dark:hover:text-blue-200"
            aria-label={unreadCount > 0 ? `Abrir Central de Notificações: ${unreadCount} não lida(s) carregada(s)` : (hasUnread ? 'Abrir Central de Notificações: há notificações não lidas' : 'Abrir Central de Notificações')}
            title="Central de Notificações"
          >
            <Bell size={20} aria-hidden="true" />
            {hasUnread && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-blue-600" aria-hidden="true" />}
          </Link>
        )}

        {isDashboard ? (
          <div className="server-reference-profile" title={userName}>
            {profilePhotoUrl ? (
              <img src={profilePhotoUrl} alt="" className="h-full w-full rounded-full object-cover" aria-hidden="true" />
            ) : (
              <span>{userInitials}</span>
            )}
            <i aria-hidden="true" />
          </div>
        ) : (
          <div className="hidden min-w-0 items-center gap-3 rounded-xl border border-[var(--sisdp-color-border)] bg-[var(--sisdp-color-surface-subtle)] px-3 py-2 xl:flex">
            <div className="min-w-0 text-right">
              <p className="max-w-40 truncate text-sm font-bold text-[var(--sisdp-color-text)]">{userName}</p>
              {vinculoSelecionado?.matricula && (
                <p className="text-[11px] text-[var(--sisdp-color-text-muted)]">Mat. {vinculoSelecionado.matricula}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
